/*
 * @providesModule SyncFTUEController
 */

import AWSManager from 'AWSManager';
import {SYNC_FTUE_NTH_MATCH_COMPLETE, HYBRID_FTUE_NTH_MATCH_COMPLETE} from 'AWSConstants';
import ClientPropertyConstants from 'ClientPropertyConstants';
import FastMatchController, {ConfirmedMatchHandler} from 'FastMatchController';
import FTUEController from 'FTUEController';
import Preferences from 'Preferences';
import SKZTournament from 'SKZTournament';
import {SKZGameMode} from 'SKZGameMode';
import SyncMatchMakingController from 'SyncMatchMakingController';
import SKZTournamentCategories from 'SKZTournamentCategories';
import SKZTournamentTemplateSet from 'SKZTournamentTemplateSet';

import {NativeModules} from 'react-native';
import FTUEConversionsController from 'FTUEConversionsController';

const SkillzModule = NativeModules.SkillzModule;

export const SyncFTUEConstants = {
  HAS_COMPLETED_SYNC_FTUE: 'hasCompletedSyncFTUESKZ',
  FIRST_VERSUS_SEEN: 'hasSeenFirstSyncFTUEVersusSKZ',
  FIRST_RESULTS_SEEN: 'hasSeenFirstSyncFTUEResultsSKZ',
  MATCH_SELECT_SEEN: 'hasSeenSyncFTUEMatchSelectSKZ',
  FAST_MATCH_SEEN: 'hasSeensyncFTUEFastMatchSKZ',
  SECOND_VERSUS_SEEN: 'hasSeenSecondSyncFTUEVersusSKZ',
  SECOND_RESULTS_SEEN: 'hasSeenSecondSyncFTUEResultsSKZ',
  NTH_GAME_VERSUS_SEEN: 'syncFTUENumNthVersusSeenSKZ',
  NTH_GAME_RESULTS_SEEN: 'syncFTUENumNthResultsSeenSKZ',
  MATCH_STREAM_SEEN: 'hasSeenSyncFTUEMatchStreamSKZ',
  FIRST_WIN_SEEN: 'hasSeenSyncFTUEFirstWin',
  FIRST_LOSS_SEEN: 'hasSeenSyncFTUEFirstLoss',
  FIRST_PENDING_SEEN: 'hasSeenSyncFTUEPendingLoss',
};

export const FTUE_TYPE = {
  ASYNC: 'Async',
  SYNC: 'Sync',
  HYBRID: 'Hybrid',
};

const NUM_REQUIRED_GAMES = 2;

type FTUEBotMatchOptions = {
  tournament: SKZTournament;
  templateSet?: SKZGameMode;
  onConfirmMatch?: ConfirmedMatchHandler;
};

class SyncFTUEController {
  canShowSyncFTUE(): boolean {
    const hasCompletedFTUE =
      this.isSyncFTUEComplete() || FTUEController.hasSeenMatchStreamTutorial();
    const gameHasSyncBot = SkillzModule.App.GameHasSyncBot;
    const correctFTUEType = this.checkFTUEScreenEligibility();
    return !hasCompletedFTUE && gameHasSyncBot && correctFTUEType;
  }

  // FALSE FOR ALL ASYNC GAMES
  checkFTUEScreenEligibility(): boolean {
    if (
      ClientPropertyConstants.getFTUEType() === FTUE_TYPE.SYNC &&
      !SkillzModule.App.GameHasSyncBot
    ) {
      return false;
    }

    return (
      (ClientPropertyConstants.getFTUEType() === FTUE_TYPE.SYNC ||
        ClientPropertyConstants.getFTUEType() === FTUE_TYPE.HYBRID) &&
      SkillzModule.App.GameHasSyncBot
    );
  }

  // FALSE FOR ALL ASYNC GAMES
  shouldSkipFTUESyncBotMissing(): boolean {
    if (
      ClientPropertyConstants.getFTUEType() === FTUE_TYPE.SYNC &&
      !SkillzModule.App.GameHasSyncBot
    ) {
      return true;
    }

    return false;
  }

  isSyncOrHybridFTUE(): boolean {
    return (
      ClientPropertyConstants.getFTUEType() === FTUE_TYPE.SYNC ||
      ClientPropertyConstants.getFTUEType() === FTUE_TYPE.HYBRID
    );
  }

  startFirstSyncBotMatch({tournament, templateSet, onConfirmMatch}: FTUEBotMatchOptions) {
    FTUEController.setHasJoinedFirstFTUEMatch(true);

    if (FTUEConversionsController.enabled) {
      FastMatchController.startHeadlessSyncMatch(tournament, {
        isBotMatch: true,
        onConfirmMatch,
      });
      return;
    }

    if (ClientPropertyConstants.getFastMatchV2Enabled()) {
      FastMatchController.startHeadlessSyncMatch(tournament, {isBotMatch: true});
      return;
    }
    // isBotMatch, headlessSearch, headlessDequeue and headlessConfirm set to true
    SyncMatchMakingController.startSearchingForSyncMatch(
      tournament,
      false,
      templateSet?.id,
      true,
      true,
      true,
      true
    );
  }

  getTournamentForSyncFTUE(
    gameModes: SKZGameMode[]
  ): {gameMode: SKZGameMode; tournament: SKZTournament} | null {
    if (gameModes.length < 1) {
      return null;
    }

    let cheapestGameMode: SKZGameMode | null = null;
    let cheapestTemplate: SKZTournament | null = null;

    gameModes.forEach((gameMode) => {
      gameMode?.tournaments.forEach((template: SKZTournament) => {
        if (!template.isZTournament()) {
          return;
        }

        if (!cheapestTemplate || template.z_fee < cheapestTemplate.z_fee) {
          cheapestTemplate = template;
          cheapestGameMode = gameMode;
          return;
        }
      });
    });

    if (!cheapestGameMode || !cheapestTemplate) {
      return null;
    }
    // @ts-ignore  there's a bug in the TS compiler where if you initialize variables as null and then
    // @ts-ignore  assign them in a callback function (aka exactly what we are doing here) then it
    // @ts-ignore  thinks that the type is `never` and won't let you touch it
    return {gameMode: cheapestGameMode, tournament: cheapestTemplate};
  }

  getTournamentForSyncFTUELegacy(
    tournamentCategories: SKZTournamentCategories
  ): {templateSet: SKZTournamentTemplateSet; tournament: SKZTournament} | null {
    const syncCategory = tournamentCategories.getCategoryForType('SYNC');
    if (!syncCategory?.tournament_template_sets) {
      return null;
    }

    let cheapestTemplateSet: SKZTournamentTemplateSet | null = null;
    let cheapestTemplate: SKZTournament | null = null;

    syncCategory.tournament_template_sets.forEach((templateSet) => {
      templateSet.tournaments.forEach((template) => {
        if (!template.isZTournament()) {
          return;
        }

        if (!cheapestTemplate || template.z_fee < cheapestTemplate.z_fee) {
          cheapestTemplate = template;
          cheapestTemplateSet = templateSet;
          return;
        }
      });
    });

    if (!cheapestTemplateSet || !cheapestTemplate) {
      return null;
    }
    // @ts-ignore  there's a bug in the TS compiler where if you initialize variables as null and then
    // @ts-ignore  assign them in a callback function (aka exactly what we are doing here) then it
    // @ts-ignore  thinks that the type is `never` and won't let you touch it
    return {templateSet: cheapestTemplateSet, tournament: cheapestTemplate};
  }

  resetSyncFTUE() {
    console.debug('Resetting the sync ftue');
    this.setSyncFTUEComplete(false);

    // Set first match flags to false
    this.setHasSeenFirstSyncFTUEVersus(false);
    this.setHasSeenFirstSyncFTUEResults(false);

    // Set match select and fast match flags to false
    this.setHasSeenSyncMatchSelectTutorial(false);
    this.setHasSeenFastMatchTutorial(false);

    // Set second match flags to false
    this.setHasSeenSecondSyncFTUEVersus(false);
    this.setHasSeenSecondSyncFTUEResults(false);

    // Set n-games flags back to 0
    this.resetHasSeenSyncFTUEVersusForNthGame();
    this.resetHasSeenSyncFTUEResultsForNthGame();

    // Set match stream flag back to false
    this.setHasSeenSyncMatchStreamTutorial(false);

    // Set PLOW result tooltip flags to false
    this.setHasSeenSyncFTUEFirstWin(false);
    this.setHasSeenSyncFTUEFirstLoss(false);
    this.setHasSeenSyncFTUEFirstPending(false);
  }

  setSyncFTUEComplete(complete: boolean = true) {
    Preferences.set({[SyncFTUEConstants.HAS_COMPLETED_SYNC_FTUE]: complete});
    FTUEController.setHasSeenLegacyTutorial(complete);
    FTUEController.setHasCompletedWelcomeFTUE(complete);
  }

  isSyncFTUEComplete(): boolean {
    return !!Preferences.get(SyncFTUEConstants.HAS_COMPLETED_SYNC_FTUE);
  }

  setHasSeenFirstSyncFTUEVersus(hasSeen: boolean) {
    Preferences.set({[SyncFTUEConstants.FIRST_VERSUS_SEEN]: hasSeen});
  }

  canShowFirstSyncFTUEVersus(): boolean {
    return this.canShowSyncFTUE() && !Preferences.get(SyncFTUEConstants.FIRST_VERSUS_SEEN);
  }

  setHasSeenFirstSyncFTUEResults(hasSeen: boolean) {
    Preferences.set({[SyncFTUEConstants.FIRST_RESULTS_SEEN]: hasSeen});
  }

  canShowFirstSyncFTUEResults(): boolean {
    return this.canShowSyncFTUE() && !Preferences.get(SyncFTUEConstants.FIRST_RESULTS_SEEN);
  }

  setHasSeenSyncMatchSelectTutorial(hasSeen: boolean) {
    Preferences.set({[SyncFTUEConstants.MATCH_SELECT_SEEN]: hasSeen});
  }

  canShowSyncFTUEMatchSelectTutorial(): boolean {
    return (
      !ClientPropertyConstants.getFTUESkipMatchTutorial() &&
      this.canShowSyncFTUE() &&
      (!Preferences.get(SyncFTUEConstants.MATCH_SELECT_SEEN) ||
        this.canShowSecondSyncFTUEVersus() ||
        this.canShowSyncFTUENthGameVersus()) &&
      !this.shouldSkipFTUESyncBotMissing()
    );
  }

  setHasSeenFastMatchTutorial(hasSeen: boolean) {
    Preferences.set({[SyncFTUEConstants.FAST_MATCH_SEEN]: hasSeen});
  }

  canShowSyncFTUEFastMatchTutorial(): boolean {
    return this.canShowSyncFTUE() && !Preferences.get(SyncFTUEConstants.FAST_MATCH_SEEN);
  }

  setHasSeenSecondSyncFTUEVersus(hasSeen: boolean) {
    Preferences.set({[SyncFTUEConstants.SECOND_VERSUS_SEEN]: hasSeen});
  }

  canShowSecondSyncFTUEVersus(): boolean {
    return (
      this.canShowSyncFTUE() &&
      !this.canShowFirstSyncFTUEVersus() &&
      !Preferences.get(SyncFTUEConstants.SECOND_VERSUS_SEEN)
    );
  }

  setHasSeenSecondSyncFTUEResults(hasSeen: boolean) {
    Preferences.set({[SyncFTUEConstants.SECOND_RESULTS_SEEN]: hasSeen});
  }

  canShowSecondSyncFTUEResults(): boolean {
    return (
      this.canShowSyncFTUE() &&
      !this.canShowFirstSyncFTUEResults() &&
      !Preferences.get(SyncFTUEConstants.SECOND_RESULTS_SEEN)
    );
  }

  incrementHasSeenSyncFTUEVersusForNthGame() {
    const oldSeen = Preferences.get(SyncFTUEConstants.NTH_GAME_VERSUS_SEEN) ?? 0;
    Preferences.set({[SyncFTUEConstants.NTH_GAME_VERSUS_SEEN]: oldSeen + 1});
  }

  resetHasSeenSyncFTUEVersusForNthGame() {
    Preferences.set({[SyncFTUEConstants.NTH_GAME_VERSUS_SEEN]: 0});
  }

  canShowSyncFTUENthGameVersus(): boolean {
    return (
      this.canShowSyncFTUE() &&
      !this.canShowSecondSyncFTUEVersus() &&
      !this.hasSeenNthGames(SyncFTUEConstants.NTH_GAME_VERSUS_SEEN)
    );
  }

  incrementHasSeenSyncFTUEResultsForNthGame() {
    const oldSeen = Preferences.get(SyncFTUEConstants.NTH_GAME_RESULTS_SEEN) || 0;
    const newSeen = oldSeen + 1;
    const tot = ClientPropertyConstants.getNumberSyncFTUEGames();
    const eventProps = {result: `${newSeen}/${tot}`};
    if (ClientPropertyConstants.getFTUEType() === FTUE_TYPE.SYNC) {
      AWSManager.trackEvent(SYNC_FTUE_NTH_MATCH_COMPLETE, eventProps);
    }
    if (ClientPropertyConstants.getFTUEType() === FTUE_TYPE.HYBRID) {
      AWSManager.trackEvent(HYBRID_FTUE_NTH_MATCH_COMPLETE, eventProps);
    }
    Preferences.set({[SyncFTUEConstants.NTH_GAME_RESULTS_SEEN]: newSeen});
  }

  resetHasSeenSyncFTUEResultsForNthGame() {
    Preferences.set({[SyncFTUEConstants.NTH_GAME_RESULTS_SEEN]: 0});
  }

  canShowSyncFTUEPlayAgainTutorial(): boolean {
    return (
      this.canShowSyncFTUE() &&
      this.canShowSyncFTUENthGameVersus() &&
      !this.hasSeenNthGames(SyncFTUEConstants.NTH_GAME_VERSUS_SEEN)
    );
  }

  setHasSeenSyncMatchStreamTutorial(hasSeen: boolean) {
    Preferences.set({[SyncFTUEConstants.MATCH_STREAM_SEEN]: hasSeen});
  }

  canShowSyncFTUEMatchStreamTutorial(): boolean {
    return (
      this.canShowSyncFTUE() &&
      !this.canShowSyncFTUEMatchSelectTutorial() &&
      !Preferences.get(SyncFTUEConstants.MATCH_STREAM_SEEN)
    );
  }

  shouldForceMatchSelect(): boolean {
    const hasSeenNthGames = this.hasSeenNthGames(SyncFTUEConstants.NTH_GAME_VERSUS_SEEN);
    return this.canShowSyncFTUE() && (this.canShowSecondSyncFTUEVersus() || !hasSeenNthGames);
  }

  shouldForceMatchStream(): boolean {
    const hasSeenNthGames = this.hasSeenNthGames(SyncFTUEConstants.NTH_GAME_VERSUS_SEEN);
    return (
      this.canShowSyncFTUEMatchStreamTutorial() &&
      !this.canShowSecondSyncFTUEVersus() &&
      hasSeenNthGames
    );
  }

  hasSeenNthGames(syncFTUEConstant: string): boolean {
    const totalGames = ClientPropertyConstants.getNumberSyncFTUEGames();
    const nthGames = totalGames - NUM_REQUIRED_GAMES;
    const nthGameResultsSeen = Preferences.get(syncFTUEConstant) ?? 0;
    return nthGameResultsSeen >= nthGames;
  }

  canShowSyncResultsFTUE(): boolean {
    return !(!this.isSyncFTUEComplete() && FTUEController.hasSeenMatchStreamTutorial());
  }

  canShowSyncFTUEFirstWin(): boolean {
    return (
      this.canShowSyncResultsFTUE() &&
      this.checkFTUEScreenEligibility() &&
      !Preferences.get(SyncFTUEConstants.FIRST_WIN_SEEN)
    );
  }

  setHasSeenSyncFTUEFirstWin(hasSeen: boolean) {
    Preferences.set({[SyncFTUEConstants.FIRST_WIN_SEEN]: hasSeen});
  }

  canShowSyncFTUEFirstLoss(): boolean {
    return (
      this.canShowSyncResultsFTUE() &&
      this.checkFTUEScreenEligibility() &&
      !Preferences.get(SyncFTUEConstants.FIRST_LOSS_SEEN)
    );
  }

  setHasSeenSyncFTUEFirstLoss(hasSeen: boolean) {
    Preferences.set({[SyncFTUEConstants.FIRST_LOSS_SEEN]: hasSeen});
  }

  canShowSyncFTUEFirstPending(): boolean {
    return (
      this.canShowSyncResultsFTUE() &&
      this.checkFTUEScreenEligibility() &&
      !Preferences.get(SyncFTUEConstants.FIRST_PENDING_SEEN)
    );
  }

  setHasSeenSyncFTUEFirstPending(hasSeen: boolean) {
    Preferences.set({[SyncFTUEConstants.FIRST_PENDING_SEEN]: hasSeen});
  }

  // Returns the number of matches past the required 2 that have been seen
  getNthVersusSeenCount(): number {
    return Preferences.get(SyncFTUEConstants.NTH_GAME_VERSUS_SEEN) ?? 0;
  }
}

export default new SyncFTUEController();
